import { useEffect, useMemo, useState } from "react";
import {
  getMessaging,
  saveMessagingProvider,
  sendMessagingTest,
  type MessagingChannel,
} from "../lib/api.ts";
import { MessagingTemplates } from "./MessagingTemplates.tsx";
import { MessagingEvents } from "./MessagingEvents.tsx";

// ── Provider catalog ─────────────────────────────────────────────────────────
// Each entry describes the provider's id, the human label, and the fields the
// dashboard renders for its config form. Field types: string | password.

interface FieldDef {
  key: string;
  label: string;
  type?: "text" | "password" | "number";
  placeholder?: string;
  required?: boolean;
}

interface ProviderDef {
  id: string;
  label: string;
  fields: FieldDef[];
  hint?: string;
}

const EMAIL_PROVIDERS: ProviderDef[] = [
  {
    id: "resend",
    label: "Resend",
    fields: [
      { key: "api_key", label: "API key", type: "password", required: true, placeholder: "re_…" },
      { key: "from_email", label: "From email", required: true, placeholder: "hello@yourdomain.com" },
      { key: "from_name", label: "From name", placeholder: "Your business" },
    ],
    hint: "Add your domain on resend.com first; verified senders only.",
  },
  {
    id: "sendgrid",
    label: "SendGrid",
    fields: [
      { key: "api_key", label: "API key", type: "password", required: true, placeholder: "SG.…" },
      { key: "from_email", label: "From email", required: true },
      { key: "from_name", label: "From name" },
    ],
    hint: "Use a Single Sender or authenticated domain in SendGrid.",
  },
  {
    id: "mailgun",
    label: "Mailgun",
    fields: [
      { key: "api_key", label: "API key", type: "password", required: true, placeholder: "key-…" },
      { key: "domain", label: "Sending domain", required: true, placeholder: "mg.yourdomain.com" },
      { key: "region", label: "Region (us / eu)", placeholder: "us" },
      { key: "from_email", label: "From email", required: true },
      { key: "from_name", label: "From name" },
    ],
    hint: "Region must match the Mailgun region for the domain.",
  },
  {
    id: "postmark",
    label: "Postmark",
    fields: [
      { key: "server_token", label: "Server token", type: "password", required: true, placeholder: "POSTMARK_…" },
      { key: "from_email", label: "From email", required: true },
      { key: "from_name", label: "From name" },
    ],
  },
  {
    id: "ses",
    label: "AWS SES",
    fields: [
      { key: "access_key", label: "Access key ID", type: "password", required: true, placeholder: "AKIA…" },
      { key: "secret_key", label: "Secret access key", type: "password", required: true },
      { key: "region", label: "Region", required: true, placeholder: "us-east-1" },
      { key: "from_email", label: "From email", required: true },
      { key: "from_name", label: "From name" },
    ],
    hint: "Sender must be a verified identity in this SES region.",
  },
  {
    id: "smtp",
    label: "Generic SMTP (coming soon)",
    fields: [
      { key: "host", label: "Host", required: true, placeholder: "smtp.example.com" },
      { key: "port", label: "Port", type: "number", required: true, placeholder: "587" },
      { key: "username", label: "Username", required: true },
      { key: "password", label: "Password", type: "password", required: true },
      { key: "from_email", label: "From email", required: true },
      { key: "from_name", label: "From name" },
    ],
    hint: "Generic SMTP needs a sidecar — pick an HTTPS provider for now.",
  },
];

const SMS_PROVIDERS: ProviderDef[] = [
  {
    id: "twilio",
    label: "Twilio",
    fields: [
      { key: "account_sid", label: "Account SID", required: true, placeholder: "AC…" },
      { key: "auth_token", label: "Auth token", type: "password", required: true },
      { key: "from_number", label: "From number", required: true, placeholder: "+15551234567" },
    ],
  },
  {
    id: "messagebird",
    label: "MessageBird (Bird)",
    fields: [
      { key: "api_key", label: "API key", type: "password", required: true },
      { key: "originator", label: "Originator (number or alphanumeric)", required: true },
    ],
  },
  {
    id: "vonage",
    label: "Vonage",
    fields: [
      { key: "api_key", label: "API key", required: true },
      { key: "api_secret", label: "API secret", type: "password", required: true },
      { key: "from", label: "From (number or alphanumeric)", required: true },
    ],
  },
  {
    id: "plivo",
    label: "Plivo",
    fields: [
      { key: "auth_id", label: "Auth ID", required: true },
      { key: "auth_token", label: "Auth token", type: "password", required: true },
      { key: "from_number", label: "From number", required: true },
    ],
  },
  {
    id: "sns",
    label: "AWS SNS",
    fields: [
      { key: "access_key", label: "Access key ID", type: "password", required: true, placeholder: "AKIA…" },
      { key: "secret_key", label: "Secret access key", type: "password", required: true },
      { key: "region", label: "Region", required: true, placeholder: "us-east-1" },
      { key: "sender_id", label: "Sender ID (optional, where supported)" },
    ],
    hint: "SNS pricing varies by destination country — check AWS pricing page.",
  },
];

function ProviderForm({
  channel, providers, current, onSaved,
}: {
  channel: MessagingChannel;
  providers: ProviderDef[];
  current: { provider: string; config: Record<string, unknown> } | null;
  onSaved: (provider: string, config: Record<string, unknown>) => void;
}) {
  const [providerId, setProviderId] = useState<string>(current?.provider ?? providers[0].id);
  const def = useMemo(() => providers.find((p) => p.id === providerId)!, [providers, providerId]);

  const [config, setConfig] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    if (current && current.provider === providerId) {
      for (const [k, v] of Object.entries(current.config)) out[k] = String(v ?? "");
    }
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // When the provider changes, clear the form unless we have saved config
  // for that provider already (which we don't track per-provider in v1).
  useEffect(() => {
    if (current && current.provider === providerId) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(current.config)) out[k] = String(v ?? "");
      setConfig(out);
    } else {
      setConfig({});
    }
  }, [providerId, current]);

  const setField = (k: string, v: string) => setConfig((c) => ({ ...c, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      // Coerce numeric fields.
      const coerced: Record<string, unknown> = { ...config };
      for (const f of def.fields) {
        if (f.type === "number" && typeof coerced[f.key] === "string") {
          const n = Number(coerced[f.key]);
          if (!Number.isNaN(n)) coerced[f.key] = n;
        }
      }
      await onSaved(providerId, coerced);
      setSavedAt(Date.now());
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <label className="label" htmlFor={`${channel}-provider`}>Provider</label>
        <select
          id={`${channel}-provider`}
          className="input"
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        {def.hint && (
          <p style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 4 }}>{def.hint}</p>
        )}
      </div>

      {def.fields.map((f) => (
        <div key={f.key}>
          <label className="label" htmlFor={`${channel}-${f.key}`}>
            {f.label}{f.required && " *"}
          </label>
          <input
            id={`${channel}-${f.key}`}
            className="input"
            type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"}
            placeholder={f.placeholder}
            value={config[f.key] ?? ""}
            onChange={(e) => setField(f.key, e.target.value)}
            required={f.required}
            autoComplete={f.type === "password" ? "new-password" : "off"}
          />
        </div>
      ))}

      {err && <div className="alert-error" style={{ fontSize: 13 }}>{err}</div>}

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button type="submit" className="zd-hero-cta" disabled={saving}>
          {saving ? "Saving…" : `Save ${channel} provider`}
        </button>
        {savedAt && (
          <span style={{ fontSize: 12, color: "var(--ink-faint)" }}>Saved.</span>
        )}
      </div>
    </form>
  );
}

function TestSendForm({ channel, slug }: { channel: MessagingChannel; slug: string }) {
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!to) return;
    setSending(true);
    setResult(null);
    try {
      const r = await sendMessagingTest(slug, { channel, to });
      setResult({ ok: true, msg: r.providerMessageId ? `Sent (id: ${r.providerMessageId})` : "Sent." });
    } catch (e2) {
      setResult({ ok: false, msg: (e2 as Error).message });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap", marginTop: 12 }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <label className="label">{channel === "email" ? "Test email address" : "Test phone (E.164)"}</label>
        <input
          className="input"
          type={channel === "email" ? "email" : "tel"}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={channel === "email" ? "you@example.com" : "+15551234567"}
        />
      </div>
      <button type="submit" disabled={sending || !to} className="zd-hero-cta">
        {sending ? "Sending…" : "Send test"}
      </button>
      {result && (
        <div
          className={result.ok ? "" : "alert-error"}
          style={{
            flexBasis: "100%", marginTop: 6, fontSize: 13,
            color: result.ok ? "var(--status-done)" : undefined,
          }}
        >
          {result.msg}
        </div>
      )}
    </form>
  );
}

export function MessagingTab({ slug }: { slug: string }) {
  const [state, setState] = useState<{ email: { provider: string; config: Record<string, unknown> } | null; sms: { provider: string; config: Record<string, unknown> } | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getMessaging(slug)
      .then((d) => setState({ email: d.email ?? null, sms: d.sms ?? null }))
      .catch((e) => setErr((e as Error).message));
  }, [slug]);

  if (err) return <div className="alert-error">{err}</div>;
  if (!state) return <div style={{ color: "var(--ink-faint)" }}>Loading…</div>;

  const handleSave = (channel: MessagingChannel) =>
    async (provider: string, config: Record<string, unknown>) => {
      await saveMessagingProvider(slug, channel, { provider, config });
      // Optimistic local update so the form shows what was saved.
      setState((s) => s && ({ ...s, [channel]: { provider, config } }));
    };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 56 }}>
      {/* ── EMAIL ── */}
      <section>
        <h2 className="zd-section-title">Email</h2>
        <p style={{ color: "var(--ink-soft)", fontSize: 14, marginTop: 4, marginBottom: 20 }}>
          Pick a provider, paste your API key + sender details. Zedule never sees your key
          beyond storing it for sends from your tenant.
        </p>
        <ProviderForm
          channel="email"
          providers={EMAIL_PROVIDERS}
          current={state.email}
          onSaved={handleSave("email")}
        />
        {state.email && <TestSendForm channel="email" slug={slug} />}
      </section>

      {/* ── SMS ── */}
      <section>
        <h2 className="zd-section-title">SMS</h2>
        <p style={{ color: "var(--ink-soft)", fontSize: 14, marginTop: 4, marginBottom: 20 }}>
          Used for appointment reminders. SMS costs are billed by your provider, not by us.
        </p>
        <ProviderForm
          channel="sms"
          providers={SMS_PROVIDERS}
          current={state.sms}
          onSaved={handleSave("sms")}
        />
        {state.sms && <TestSendForm channel="sms" slug={slug} />}
      </section>

      {/* ── Per-event channel toggles ── */}
      <MessagingEvents slug={slug} />

      {/* ── Templates ── */}
      <MessagingTemplates slug={slug} />
    </div>
  );
}
